import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card, CardContent, Box, Avatar, Typography, List, ListItemButton, ListItemContent,
  ListDivider, Chip, Alert, Button, CircularProgress, IconButton,
} from '@mui/joy';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

import { APIBookingMy, APIBookingCancel } from '../../api';

const STATUS = {
  booked: { t: 'Записан', c: 'primary' },
  waiting: { t: 'Ожидает', c: 'warning' },
  in_progress: { t: 'На приёме', c: 'primary' },
  done: { t: 'Завершён', c: 'success' },
  canceled: { t: 'Отменён', c: 'danger' },
  unknown: { t: '—', c: 'neutral' },
};

function fmt(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return String(dt);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const MIN_INTERVAL_MS = 3000; // не чаще одного обновления раз в 3 сек (лимит МИС — 1 запрос/сек)

export default function MyAppointmentsCard({ token, refreshKey, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [synced, setSynced] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cancelingId, setCancelingId] = useState(null);

  const inFlightRef = useRef(false); // защита от параллельных запросов (в т.ч. двойной вызов в StrictMode)
  const lastLoadRef = useRef(0);     // время последней загрузки — для троттлинга ручного «обновить»

  const load = useCallback((force = false) => {
    if (!token) return;
    if (inFlightRef.current) return;                       // уже грузим — второй запрос не пускаем
    const now = Date.now();
    if (!force && now - lastLoadRef.current < MIN_INTERVAL_MS) return; // троттлинг ручных обновлений
    lastLoadRef.current = now;
    inFlightRef.current = true;
    setLoading(true); setError(''); setNotice('');
    APIBookingMy(token)
      .then((d) => {
        setSynced(d?.synced ?? true);
        if (d?.stale) {
          // сервис был занят (429): список не обновился — показываем подсказку, старые записи оставляем
          if (d?.notice) setNotice(d.notice);
        } else {
          setItems(d?.appointments || []);
        }
      })
      .catch((e) => setError(e?.response?.data?.error || 'Не удалось загрузить записи'))
      .finally(() => { inFlightRef.current = false; setLoading(false); });
  }, [token]);

  // на открытие/после брони грузим принудительно (force), StrictMode-дубль отсекает inFlightRef
  useEffect(() => { if (token) load(true); }, [token, refreshKey, load]);

  const cancel = async (appt) => {
    if (!appt.local_booking_id) return;
    setCancelingId(appt.local_booking_id);
    try { await APIBookingCancel(token, appt.local_booking_id); load(); onChanged?.(); }
    catch (e) { setError(e?.response?.data?.error || 'Не удалось отменить запись'); }
    finally { setCancelingId(null); }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Avatar variant="soft" color="primary" sx={{ '--Avatar-size': '40px' }}>
            <EventAvailableRoundedIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography level="title-md">Мои записи</Typography>
            <Typography level="body-sm" textColor="text.tertiary">Ваши визиты в клинику</Typography>
          </Box>
          <IconButton size="sm" variant="plain" onClick={() => load()} disabled={loading}><RefreshRoundedIcon /></IconButton>
        </Box>

        {notice && <Alert color="neutral" variant="soft" sx={{ mb: 1 }}>{notice}</Alert>}

        {loading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
          : error
            ? <Alert color="danger" variant="soft">{error}</Alert>
            : !synced
              ? <Alert color="neutral" variant="soft">Ваш профиль ещё синхронизируется с клиникой — записи появятся позже.</Alert>
              : items.length === 0
                ? <Alert color="neutral" variant="soft">Активных записей нет.</Alert>
                : <List sx={{ mt: 0.5 }}>
                    {items.map((a, idx) => {
                      const st = STATUS[a.status] || STATUS.unknown;
                      const canCancel = a.local_booking_id && (a.status === 'booked' || a.status === 'waiting');
                      return (
                        <Box key={a.mis_appointment_id || idx}>
                          {idx > 0 && <ListDivider />}
                          <ListItemButton sx={{ borderRadius: 'sm' }}>
                            {a.doctor_name && (
                              <Avatar size="sm" src={a.doctor_avatar_url || undefined} sx={{ mr: 1.5 }}>
                                {(a.doctor_name || '?').slice(0, 1)}
                              </Avatar>
                            )}
                            <ListItemContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                                <Typography level="title-sm">{fmt(a.datetime)}</Typography>
                                <Chip size="sm" color={st.c} variant="soft">{st.t}</Chip>
                              </Box>
                              {a.doctor_name && <Typography level="body-sm" textColor="text.tertiary">{a.doctor_name}</Typography>}
                              {a.service && <Typography level="body-xs" textColor="text.tertiary">{a.service}</Typography>}
                              {a.clinic && <Typography level="body-xs" textColor="text.tertiary">{a.clinic}</Typography>}
                            </ListItemContent>
                            {canCancel && (
                              <Button size="sm" variant="plain" color="danger"
                                loading={cancelingId === a.local_booking_id}
                                onClick={(e) => { e.stopPropagation(); cancel(a); }}>
                                Отменить
                              </Button>
                            )}
                          </ListItemButton>
                        </Box>
                      );
                    })}
                  </List>}
      </CardContent>
    </Card>
  );
}